import * as React from "react";
import * as RadixToast from "@radix-ui/react-toast";
import { cn } from "lib/utils";

type ToastVariant = "default" | "success" | "error" | "warning" | "info";

export type ToastOptions = {
  title?: string;
  description?: string | React.ReactNode;
  variant?: ToastVariant;
  duration?: number; // ms
  action?: { label: string; onClick: () => void } | null;
};

type Listener = (t: InternalToast) => void;
const listeners = new Set<Listener>();
let _id = 0;

type InternalToast = ToastOptions & { id: number };

function pushToast(t: ToastOptions) {
  const item: InternalToast = { id: ++_id, duration: 3500, variant: "default", ...t };
  listeners.forEach((cb) => cb(item));
  return item.id;
}

export const toast = Object.assign(
  (opts: ToastOptions) => pushToast(opts),
  {
    success: (opts: ToastOptions | string) =>
      pushToast(typeof opts === "string" ? { title: opts, variant: "success" } : { ...opts, variant: "success" }),
    error: (opts: ToastOptions | string) =>
      pushToast(typeof opts === "string" ? { title: opts, variant: "error" } : { ...opts, variant: "error" }),
    info: (opts: ToastOptions | string) =>
      pushToast(typeof opts === "string" ? { title: opts, variant: "info" } : { ...opts, variant: "info" }),
    warning: (opts: ToastOptions | string) =>
      pushToast(typeof opts === "string" ? { title: opts, variant: "warning" } : { ...opts, variant: "warning" }),

    // promise wrapper
    promise<T>(
      p: Promise<T>,
      messages: {
        loading: string | ToastOptions;
        success: string | ((value: T) => string | ToastOptions) | ToastOptions;
        error: string | ((err: any) => string | ToastOptions) | ToastOptions;
      }
    ) {
      const loadingId = pushToast(
        typeof messages.loading === "string"
          ? { title: messages.loading }
          : { ...messages.loading }
      );

      p.then((val) => {
        // replace loading with success
        toast.dismiss(loadingId);
        const msg = typeof messages.success === "function" ? messages.success(val) : messages.success;
        typeof msg === "string" ? toast.success(msg) : toast.success(msg);
      }).catch((err) => {
        toast.dismiss(loadingId);
        const msg = typeof messages.error === "function" ? messages.error(err) : messages.error;
        typeof msg === "string" ? toast.error(msg) : toast.error(msg);
      });

      return p;
    },

    // allow manual dismiss by id
    dismiss(id?: number) {
      listeners.forEach((cb) =>
        cb({ id: id ?? -1, title: "__dismiss__", variant: "default", duration: 0 })
      );
    },
  }
);

export function Toaster() {
  const [items, setItems] = React.useState<InternalToast[]>([]);

  React.useEffect(() => {
    const handler: Listener = (t) => {
      if (t.title === "__dismiss__") {
        setItems((prev) => (t.id === -1 ? [] : prev.filter((i) => i.id !== t.id)));
        return;
      }
      setItems((prev) => [...prev, t]);
    };
    listeners.add(handler);
    return () => void listeners.delete(handler);
  }, []);

  const remove = (id: number) => setItems((prev) => prev.filter((i) => i.id !== id));

  return (
    <RadixToast.Provider swipeDirection="right">
      {items.map((t) => (
        <RadixToast.Root
          key={t.id}
          duration={t.duration}
          onOpenChange={(open) => !open && remove(t.id)}
          className={cn(
            "card pointer-events-auto w-[min(420px,92vw)]",
            "fade-in slide-in-right",
            "border flex rounded-lg shadow-lg px-4 py-3",
            t.description ? "items-start gap-3" : "items-center gap-3",
            variantClass(t.variant)
          )}
        >
          {/* Icon */}
          <div className={cn("flex-shrink-0", t.description && "mt-0.5")}>
            {getVariantIcon(t.variant)}
          </div>

          <div className="flex-1 min-w-0">
            {t.title && (
              <RadixToast.Title className={cn(
                "font-medium",
                t.description ? "mb-1" : "mb-0",
                getTitleColor(t.variant)
              )}>
                {t.title}
              </RadixToast.Title>
            )}
            {t.description && (
              <RadixToast.Description className="text-sm opacity-90">
                {t.description}
              </RadixToast.Description>
            )}
          </div>

          {t.action && (
            <RadixToast.Action asChild altText={t.action.label}>
              <button
                className="btn-ghost text-sm flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  t.action?.onClick?.();
                }}
              >
                {t.action.label}
              </button>
            </RadixToast.Action>
          )}

          <RadixToast.Close className="btn-ghost p-1 rounded-md ml-1 flex-shrink-0 -mr-1 -mt-1" aria-label="Close">
            ✕
          </RadixToast.Close>
        </RadixToast.Root>
      ))}

      <RadixToast.Viewport
        className={cn(
          "fixed z-[999999] bottom-4 right-4",
          "flex flex-col gap-2 outline-none"
        )}
      />
    </RadixToast.Provider>
  );
}

function variantClass(variant: ToastVariant) {
  switch (variant) {
    case "success":
      return "border-green-500/20 bg-green-50 dark:bg-green-950/90 dark:border-green-700/50 text-green-800 dark:text-green-200";
    case "error":
      return "border-red-500/20 bg-red-50 dark:bg-red-950/90 dark:border-red-500/50 text-red-800 dark:text-red-100";
    case "warning":
      return "border-yellow-500/20 bg-yellow-50 dark:bg-yellow-950/90 dark:border-yellow-500/50 text-yellow-800 dark:text-yellow-100";
    case "info":
      return "border-blue-500/20 bg-blue-50 dark:bg-blue-950/90 dark:border-blue-500/50 text-blue-800 dark:text-blue-100";
    default:
      return "border-line  dark:bg-dark-800 dark:border-dark-600 text-gray-800 dark:text-gray-100";
  }
}

function getTitleColor(variant: ToastVariant) {
  switch (variant) {
    case "success":
      return "text-green-900 dark:text-green-100";
    case "error":
      return "text-red-900 dark:text-red-50";
    case "warning":
      return "text-yellow-900 dark:text-yellow-50";
    case "info":
      return "text-blue-900 dark:text-blue-50";
    default:
      return "text-content dark:text-gray-100";
  }
}

function getVariantIcon(variant: ToastVariant) {
  switch (variant) {
    case "success":
      return (
        <svg className="w-5 h-5 text-green-600 dark:text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "error":
      return (
        <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "warning":
      return (
        <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case "info":
      return (
        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5 text-content-secondary dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
}
